"use client"

import { useEffect, useMemo } from "react"
import { AvatarGroup } from "./AvatarGroup"
import { Channel, UR } from "stream-chat"
import { Separator, Text } from "@/elements"
import { BellSlashIcon } from "@heroicons/react/24/outline"
import { PlayIcon } from "@heroicons/react/24/solid"
import clsx from "clsx"
import { ChannelPreviewUIComponentProps, ChatContextValue, useChatContext } from "stream-chat-react"
import { StreamChatGenerics } from "./types"
import { useMessages } from "./hooks/useMessages"
import { getChannelName, getTimeStamp } from "./utils"

interface Props extends ChannelPreviewUIComponentProps {
  onClick: () => void
  channel: Channel
  index: number
  setActiveChannel?: ChatContextValue["setActiveChannel"]
  showJobTitle?: boolean
}

export const ChannelPreview: React.FC<Props> = ({
  onClick,
  setActiveChannel,
  channel,
  index,
  showJobTitle = true,
  ...rest
}) => {
  const { client } = useChatContext()
  const { mutedChannels, setMutedChannels, activeSideBarIndex, setActiveSideBarIndex } = useMessages()

  const members = Object.values(channel.state.members).filter(({ user }) => user?.id !== client?.userID)

  const active = rest?.activeChannel?.cid === channel.cid
  const lastMessage = channel.state.messages[channel.state.messages.length - 1]

  const title = useMemo(() => {
    return channel?.data?.name ?? getChannelName(members)
  }, [channel, members])

  useEffect(() => {
    if (channel) {
      const isMuted = channel.muteStatus().muted
      setMutedChannels({
        [channel.id!]: isMuted,
      })
    }
  }, [channel, setMutedChannels])

  const isMuted = channel?.id ? mutedChannels[channel?.id!] : false

  useEffect(() => {
    if (active) {
      setActiveSideBarIndex?.(index)
    }
  }, [active, setActiveSideBarIndex, index])

  const topic: string | undefined = channel?.data?.topic ? `${channel?.data?.topic}` : undefined

  const handleOnClick = () => {
    onClick()
    setActiveChannel?.(channel)
  }

  return (
    <div className="group px-3">
      <div
        className={clsx("flex cursor-pointer flex-row items-center rounded-xl px-3 py-3", active && "bg-bg-2")}
        onClick={handleOnClick}>
        <div className="mr-3">
          <AvatarGroup members={members} />
        </div>
        <div className="flex w-full flex-1 flex-col overflow-hidden">
          <div className="flex w-full flex-1 flex-row items-center justify-between">
            <Text maxLines={1} variant="label">
              {title}
            </Text>
            <Text variant="caption" className="text-fg-3">
              {getTimeStamp(channel)}
            </Text>
          </div>
          {!!topic && showJobTitle && (
            <div className="flex flex-row items-center">
              <PlayIcon height={12} width={12} className="mr-1 fill-primary-3" />
              <Text variant="body3" className="text-primary-3">
                {topic}
              </Text>
            </div>
          )}
          <div className="mt-1 flex flex-row items-start">
            <div className="flex flex-1">
              <Text
                maxLines={2}
                variant="body3"
                className={clsx("w-full text-fg-2 group-hover:text-fg-1", active && "text-fg-1")}>
                {lastMessage?.text || "No messages"}
              </Text>
            </div>
            <div className="pl-6">
              {isMuted ? (
                <BellSlashIcon height={16} />
              ) : (
                <div>
                  <div
                    className={clsx(
                      "flex size-[24px] items-center justify-center rounded-full bg-primary-2",
                      channel?.state?.unreadCount && channel?.state?.unreadCount > 0 ? "opacity-100" : "opacity-0"
                    )}>
                    <Text variant="body3">{channel?.state?.unreadCount}</Text>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={clsx("px-3", (active || index === activeSideBarIndex - 1) && "opacity-0")}>
        <Separator />
      </div>
    </div>
  )
}
